import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import API_URL from '../config/api';

const styles = StyleSheet.create({
  page: { padding: 40, fontSize: 10, fontFamily: 'Helvetica', backgroundColor: '#ffffff' },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  logo: { width: 80, height: 80, objectFit: 'contain' },
  companyInfo: { textAlign: 'right', maxWidth: '50%' },
  companyName: { fontSize: 14, fontWeight: 'bold', marginBottom: 3, color: '#1a365d' },
  companyDetails: { fontSize: 8, color: '#4a5568', marginBottom: 2 },
  
  titleContainer: { marginBottom: 20, borderBottomWidth: 2, borderColor: '#2d3748', paddingBottom: 10 },
  title: { fontSize: 24, fontWeight: 'bold', color: '#2d3748' },
  period: { fontSize: 10, color: '#718096', marginTop: 4 },
  
  infoRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 30 },
  infoBlock: { width: '45%' },
  label: { fontSize: 8, color: '#a0aec0', marginBottom: 4, textTransform: 'uppercase', fontWeight: 'bold' },
  value: { fontSize: 10, color: '#2d3748', marginBottom: 2 },
  customerName: { fontSize: 12, fontWeight: 'bold', color: '#1a365d', marginBottom: 4 },

  openingBalanceContainer: { 
    backgroundColor: '#f7fafc', 
    padding: 10, 
    flexDirection: 'row', 
    justifyContent: 'space-between', 
    alignItems: 'center',
    marginBottom: 10,
    borderWidth: 1,
    borderColor: '#edf2f7',
    borderRadius: 4
  },
  openingBalanceLabel: { fontSize: 10, fontWeight: 'bold', color: '#4a5568' },
  openingBalanceValue: { fontSize: 11, fontWeight: 'bold', color: '#2d3748' },

  table: { marginTop: 10 },
  tableHeader: { 
    flexDirection: 'row', 
    backgroundColor: '#2d3748', 
    color: '#ffffff', 
    padding: 8, 
    fontWeight: 'bold', 
    fontSize: 9,
    borderRadius: 2
  },
  tableRow: { 
    flexDirection: 'row', 
    paddingVertical: 8, 
    paddingHorizontal: 8, 
    borderBottomWidth: 1, 
    borderColor: '#e2e8f0', 
    fontSize: 9,
    alignItems: 'center'
  },
  
  colDate: { width: '15%' },
  colType: { width: '12%' },
  colRef: { width: '28%' },
  colDebit: { width: '15%', textAlign: 'right' },
  colCredit: { width: '15%', textAlign: 'right' },
  colBalance: { width: '15%', textAlign: 'right', fontWeight: 'bold' },

  summaryContainer: { 
    marginTop: 30, 
    flexDirection: 'row', 
    justifyContent: 'flex-end' 
  },
  summaryBlock: { width: '40%' },
  summaryRow: { 
    flexDirection: 'row', 
    justifyContent: 'space-between', 
    paddingVertical: 4,
    borderBottomWidth: 1,
    borderColor: '#edf2f7'
  },
  summaryLabel: { fontSize: 9, color: '#4a5568' },
  summaryValue: { fontSize: 9, fontWeight: 'bold', color: '#2d3748' },
  totalDueRow: { 
    flexDirection: 'row', 
    justifyContent: 'space-between', 
    backgroundColor: '#2d3748', 
    color: '#ffffff', 
    padding: 10, 
    marginTop: 8, 
    borderRadius: 4
  },
  totalDueLabel: { fontSize: 11, fontWeight: 'bold' },
  totalDueValue: { fontSize: 13, fontWeight: 'bold' },

  footer: { 
    position: 'absolute', 
    bottom: 30, 
    left: 40, 
    right: 40, 
    textAlign: 'center', 
    fontSize: 8, 
    color: '#a0aec0', 
    borderTopWidth: 1, 
    borderColor: '#edf2f7', 
    paddingTop: 10 
  }
});

const StatementPDF: React.FC<{ 
  data: any; 
  businessSettings?: any;
}> = ({ data, businessSettings }) => {
  if (!data) return null;

  const {
    customer,
    start_date,
    end_date,
    opening_balance,
    closing_balance,
    totals,
    transactions
  } = data;

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const logoUrl = businessSettings?.logo?.startsWith('http') 
    ? businessSettings.logo 
    : businessSettings?.logo 
      ? `${API_URL}${businessSettings.logo}` 
      : null;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header Section */}
        <View style={styles.headerRow}>
          <View>
            {logoUrl && <Image src={logoUrl} style={styles.logo} />}
          </View>
          <View style={styles.companyInfo}>
            <Text style={styles.companyName}>{businessSettings?.name || 'Business Statement'}</Text>
            {businessSettings?.address && <Text style={styles.companyDetails}>{businessSettings.address}</Text>}
            {businessSettings?.city && <Text style={styles.companyDetails}>{businessSettings.city} {businessSettings.postcode}</Text>}
            {businessSettings?.email && <Text style={styles.companyDetails}>{businessSettings.email}</Text>}
            {businessSettings?.phone && <Text style={styles.companyDetails}>Tel: {businessSettings.phone}</Text>}
            {businessSettings?.vatNumber && <Text style={styles.companyDetails}>VAT: {businessSettings.vatNumber}</Text>}
          </View>
        </View>

        {/* Title */}
        <View style={styles.titleContainer}>
          <Text style={styles.title}>Statement of Account</Text>
          <Text style={styles.period}>Period: {formatDate(start_date)} to {formatDate(end_date)}</Text>
        </View>

        {/* Customer & Summary Info */}
        <View style={styles.infoRow}>
          <View style={styles.infoBlock}>
            <Text style={styles.label}>Bill To</Text>
            <Text style={styles.customerName}>{customer.name}</Text>
            {customer.companyName && <Text style={styles.value}>{customer.companyName}</Text>}
            {customer.address && <Text style={styles.value}>{customer.address}</Text>}
            {customer.city && <Text style={styles.value}>{customer.city} {customer.postcode}</Text>}
            {customer.email && <Text style={styles.value}>{customer.email}</Text>}
          </View>
          <View style={{ ...styles.infoBlock, textAlign: 'right' }}>
            <Text style={styles.label}>Statement Date</Text>
            <Text style={styles.value}>{formatDate(new Date().toISOString())}</Text>
            <Text style={{ ...styles.label, marginTop: 10 }}>Account Number</Text>
            <Text style={styles.value}>CUST-{customer.id}</Text>
          </View>
        </View>

        {/* Opening Balance */}
        <View style={styles.openingBalanceContainer}>
          <Text style={styles.openingBalanceLabel}>Balance Forward (as of {formatDate(start_date)})</Text>
          <Text style={styles.openingBalanceValue}>£{opening_balance.toFixed(2)}</Text>
        </View>

        {/* Transactions Table */}
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={styles.colDate}>Date</Text>
            <Text style={styles.colType}>Type</Text>
            <Text style={styles.colRef}>Reference</Text>
            <Text style={styles.colDebit}>Debit (+)</Text>
            <Text style={styles.colCredit}>Credit (-)</Text>
            <Text style={styles.colBalance}>Balance</Text>
          </View>

          {transactions.map((t: any, index: number) => (
            <View key={index} style={styles.tableRow}>
              <Text style={styles.colDate}>{formatDate(t.date)}</Text>
              <Text style={{ ...styles.colType, textTransform: 'capitalize' }}>{t.type}</Text>
              <Text style={styles.colRef}>{t.reference || '-'}</Text>
              <Text style={styles.colDebit}>
                {t.type === 'invoice' ? `£${t.amount.toFixed(2)}` : ''}
              </Text>
              <Text style={styles.colCredit}>
                {t.type !== 'invoice' ? `£${t.amount.toFixed(2)}` : ''}
              </Text>
              <Text style={styles.colBalance}>£{t.running_balance.toFixed(2)}</Text>
            </View>
          ))}
        </View>

        {/* Summary Block */}
        <View style={styles.summaryContainer}>
          <View style={styles.summaryBlock}>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Total Invoiced:</Text>
              <Text style={styles.summaryValue}>£{totals.invoiced.toFixed(2)}</Text>
            </View>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Total Paid:</Text>
              <Text style={styles.summaryValue}>£{totals.paid.toFixed(2)}</Text>
            </View>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Total Credits:</Text>
              <Text style={styles.summaryValue}>£{totals.credits.toFixed(2)}</Text>
            </View>
            <View style={styles.totalDueRow}>
              <Text style={styles.totalDueLabel}>Amount Outstanding:</Text>
              <Text style={styles.totalDueValue}>£{closing_balance.toFixed(2)}</Text>
            </View>
          </View>
        </View>

        <View style={styles.footer}>
          <Text>Thank you for your business. Please make payments within your agreed credit terms.</Text>
          <Text style={{ marginTop: 4 }}>This is an automated statement. For any queries, please contact {businessSettings?.email}.</Text>
        </View>
      </Page>
    </Document>
  );
};

export default StatementPDF;